import { Package, Search, Zap } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { filterActiveServices } from '@/components/pos/posCatalog'
import { resolveCategoryIcon } from '@/components/pos/posCategoryIcon'
import { useBranch } from '@/contexts/BranchContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { fetchServices } from '@/lib/api/services'
import { SALON_DESK_CHECKOUT } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {import('./posMockData').PosCatalogMode} props.catalogMode
 * @param {(mode: import('./posMockData').PosCatalogMode) => void} props.onCatalogModeChange
 * @param {number | null} props.activeCategoryId
 * @param {(categoryId: number) => void} props.onCategoryChange
 * @param {import('@/types/api').ServiceCategory[]} props.categories
 * @param {boolean} props.loadingCategories
 * @param {number} [props.retailCount]
 * @param {number} [props.quickKeyCount]
 * @param {string} props.searchQuery
 * @param {(query: string) => void} props.onSearchChange
 */
export function PosCategorySidebar({
  catalogMode,
  onCatalogModeChange,
  activeCategoryId,
  onCategoryChange,
  categories,
  loadingCategories,
  retailCount = 0,
  quickKeyCount = 0,
  searchQuery,
  onSearchChange,
}) {
  const { activeBranchId } = useBranch()
  const { t } = useUiLocale()
  const [serviceCounts, setServiceCounts] = useState({})

  useEffect(() => {
    if (!activeBranchId) {
      setServiceCounts({})
      return undefined
    }

    let active = true

    fetchServices()
      .then((items) => {
        if (!active) return

        /** @type {Record<number, number>} */
        const counts = {}

        for (const service of filterActiveServices(items)) {
          const categoryId = service.service_category_id
          counts[categoryId] = (counts[categoryId] ?? 0) + 1
        }

        setServiceCounts(counts)
      })
      .catch(() => {
        if (active) setServiceCounts({})
      })

    return () => {
      active = false
    }
  }, [activeBranchId, categories.length])

  const totalServices = useMemo(
    () => Object.values(serviceCounts).reduce((sum, count) => sum + count, 0),
    [serviceCounts],
  )

  const selectCategory = (categoryId) => {
    onCatalogModeChange('service')
    onCategoryChange(categoryId)
  }

  const selectRetail = () => onCatalogModeChange('retail')
  const selectQuick = () => onCatalogModeChange('quick')

  return (
    <aside
      id="pos-panel-sidebar"
      className="pos-v2-sidebar flex min-h-0 flex-col overflow-hidden"
      aria-label={t('pos.categories')}
    >
      <div className="shrink-0 border-b border-[#E5E7EB] p-1.5">
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t('common.search')}
            className="pos-v2-search"
            aria-label={t('common.searchCatalog')}
          />
        </div>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto py-1" aria-label="Service categories">
        <p className="pos-v2-sidebar-caption">
          {t('pos.services')} ({totalServices})
        </p>

        {loadingCategories ? (
          <div className="space-y-0.5 px-1">
            {Array.from({ length: 10 }).map((_, index) => (
              <div key={index} className="h-10 rounded skeleton-shimmer" aria-hidden />
            ))}
          </div>
        ) : (
          <ul>
            {categories.map((category) => {
              const isActive = catalogMode === 'service' && activeCategoryId === category.id
              const count = serviceCounts[category.id] ?? 0
              const CategoryIcon = resolveCategoryIcon(category)

              return (
                <li key={category.id}>
                  <button
                    type="button"
                    onClick={() => selectCategory(category.id)}
                    className={cn('pos-v2-sidebar-item', isActive && 'pos-v2-sidebar-item--active')}
                  >
                    <CategoryIcon className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-left">{category.name}</span>
                    <span className={cn('pos-v2-sidebar-badge', isActive && 'pos-v2-sidebar-badge--active')}>
                      {count}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {SALON_DESK_CHECKOUT.showRetailAndQuickKeys ? (
          <>
        <div className="my-1 border-t border-[#E5E7EB]" role="separator" />

        <ul>
          <li>
            <button
              type="button"
              onClick={selectQuick}
              className={cn(
                'pos-v2-sidebar-item',
                catalogMode === 'quick' && 'pos-v2-sidebar-item--active',
              )}
            >
              <Zap className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-left">{t('pos.quickKeys')}</span>
              <span
                className={cn(
                  'pos-v2-sidebar-badge',
                  catalogMode === 'quick' && 'pos-v2-sidebar-badge--active',
                )}
              >
                {quickKeyCount}
              </span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={selectRetail}
              className={cn(
                'pos-v2-sidebar-item',
                catalogMode === 'retail' && 'pos-v2-sidebar-item--active',
              )}
            >
              <Package className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-left">{t('pos.retail')}</span>
              <span
                className={cn(
                  'pos-v2-sidebar-badge',
                  catalogMode === 'retail' && 'pos-v2-sidebar-badge--active',
                )}
              >
                {retailCount}
              </span>
            </button>
          </li>
        </ul>
          </>
        ) : null}
      </nav>
    </aside>
  )
}
